import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../../../../components/common/button";
import { Dialog } from "../../../../components/common/dialog";
import { Input, PhoneInput } from "../../../../components/common/input";
import { Text } from "../../../../components/common/typography";
import { handleAxiosError } from "../../../../common/error_handling";
import { userService } from "../../../api_client";
import { useMe } from "../../../../swr";
import { showSuccessToast } from "../../../../utils/toasts";
import {
  OTP_LENGTH,
  isCompleteOtp,
  sanitizeOtp,
  validateEmail,
  validatePhoneNumber,
} from "../../../../utils/validation";

export type ContactKind = "email" | "phone";

const COPY: Record<ContactKind, { title: string; field: string; sentVia: string; done: string }> = {
  email: {
    title: "Change email address",
    field: "New email address",
    sentVia: "email",
    done: "Email address updated",
  },
  phone: {
    title: "Change phone number",
    field: "New phone number",
    sentVia: "SMS",
    done: "Phone number updated",
  },
};

interface ContactChangeDialogProps {
  /** Which contact detail is being changed; `null` keeps the dialog closed. */
  kind: ContactKind | null;
  onClose: () => void;
}

/**
 * Changes the account email or phone number in two steps: the new value gets
 * a 6-digit code, and the account is only updated once that code is entered.
 * Both are sign-in identifiers, so they never change without that proof.
 */
export function ContactChangeDialog({ kind, onClose }: ContactChangeDialogProps) {
  const { mutate } = useMe();
  const [step, setStep] = useState<"enter" | "verify">("enter");
  const [value, setValue] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  // Every opening starts from a blank form.
  useEffect(() => {
    if (!kind) return;
    setStep("enter");
    setValue("");
    setCode("");
    setError(undefined);
  }, [kind]);

  if (!kind) return null;
  const copy = COPY[kind];
  const target = kind === "email" ? { email: value.trim().toLowerCase() } : { phoneNumber: value };

  const sendCode = async () => {
    setBusy(true);
    try {
      await userService.requestContactChange(target);
      return true;
    } catch (err) {
      setError(handleAxiosError(err).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async (e: FormEvent) => {
    e.preventDefault();
    const invalid = kind === "email" ? validateEmail(value) : validatePhoneNumber(value);
    setError(invalid ?? undefined);
    if (invalid || busy) return;
    if (await sendCode()) setStep("verify");
  };

  const handleResend = async () => {
    setError(undefined);
    if (await sendCode()) showSuccessToast("A new code has been sent.");
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (!isCompleteOtp(code) || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await userService.verifyContactChange({ ...target, otp: code });
      await mutate();
      showSuccessToast(copy.done);
      onClose();
    } catch (err) {
      setError(handleAxiosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const content =
    step === "enter" ? (
      <form onSubmit={handleSend} className="mt-2 flex flex-col gap-5">
        <Text variant="body" tone="body">
          We'll send a {OTP_LENGTH}-digit code by {copy.sentVia} to confirm it's yours. Your
          current {kind === "email" ? "email address" : "phone number"} stays active until then.
        </Text>
        {kind === "email" ? (
          <Input
            label={copy.field}
            type="email"
            autoComplete="email"
            placeholder="name@company.com"
            value={value}
            error={error}
            onChange={(e) => {
              setValue(e.target.value);
              setError(undefined);
            }}
            required
          />
        ) : (
          <PhoneInput
            label={copy.field}
            value={value}
            error={error}
            onChange={(next) => {
              setValue(next);
              setError(undefined);
            }}
            required
          />
        )}
        <div className="flex flex-col gap-3">
          <Button variant="primary" type="submit" disabled={!value || busy} fullWidth>
            {busy ? "Sending…" : "Send code"}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={busy} fullWidth>
            Cancel
          </Button>
        </div>
      </form>
    ) : (
      <form onSubmit={handleVerify} className="mt-2 flex flex-col gap-5">
        <Text variant="body" tone="body">
          Enter the code we sent to{" "}
          <Text variant="bodyBold" tone="dark" inline className="[overflow-wrap:anywhere]">
            {kind === "email" ? target.email : value}
          </Text>
          .
        </Text>
        <Input
          label="Verification code"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={`Enter the ${OTP_LENGTH}-digit code`}
          value={code}
          error={error}
          onChange={(e) => {
            setCode(sanitizeOtp(e.target.value));
            setError(undefined);
          }}
          required
        />
        <div className="flex flex-col gap-3">
          <Button variant="primary" type="submit" disabled={!isCompleteOtp(code) || busy} fullWidth>
            {busy ? "Verifying…" : "Verify and save"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setStep("enter");
              setCode("");
              setError(undefined);
            }}
            disabled={busy}
            fullWidth
          >
            Use a different {kind === "email" ? "email address" : "phone number"}
          </Button>
        </div>
        <Text variant="body" tone="muted" className="text-center">
          Didn't get a code?{" "}
          <button
            type="button"
            onClick={handleResend}
            disabled={busy}
            className="bg-transparent disabled:opacity-50"
          >
            <Text variant="bodyBold" tone="dark" inline>
              Resend
            </Text>
          </button>
        </Text>
      </form>
    );

  return (
    <Dialog
      open
      setOpen={(next) => {
        if (!next) onClose();
      }}
      title={copy.title}
      titleAlignment="left"
      showCloseButton
      showButtons={false}
      busy={busy}
      width="max-w-md"
      content={content}
    />
  );
}
